import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react'
import http from '../../service/axiosConfig'
import {
  API_CONTRACTOR_CLOSURE_IMPORT_ROWS,
  API_CONTRACTOR_CLOSURE_IMPORTS,
  API_CONTRACTOR_CLOSURE_IMPORT_STATUS,
  API_CONTRACTOR_CLOSURE_LATEST,
  API_CONTRACTOR_CLOSURE_OPTIONS,
  API_CONTRACTOR_CLOSURE_ROW_DETAIL,
  API_CONTRACTOR_CLOSURE_ROWS,
  API_CONTRACTOR_CLOSURE_TMK_EXCEPTIONS,
} from '../../service/api.endpoint'
import GraphWellHistoryModal from '../graph/component/modal.graph.wellHistory'
import '../../component/css/agGridGraph.css'
import '../css/contractorClosure.css'

type OrgOption = {
  id: string
  name: string
}

type ClosureRow = {
  id: string
  sheet_name: string
  excel_row: number
  raw_dns: string
  raw_bush: string
  raw_well: string
  plan_from_file: string | null
  fact_from_file: string | null
  depth_from_file: string | null
  contractor_comment: string
  org_name: string
  well_id: string | null
  well_name: string
  bush_name: string
  deposit_name: string
  status: string
  status_label: string
  coefficient: string
  system_comment: string
  adjustment_comment: string
  adjusted_by_name: string
  adjusted_at: string | null
  adjustment_logs: any[]
  control_request_number: string
  details: any
}

type ClosureImport = {
  id: string
  year: number
  month: number
  org: string | null
  org_name: string
  source_file: string
  status: string
  summary: Record<string, number>
  created_at: string
  rows: ClosureRow[]
  tmk_exceptions?: any[]
}

const monthNames = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]

const statusClass: Record<string, string> = {
  CLOSED: 'closure-status closure-status_closed',
  PARTIAL: 'closure-status closure-status_partial',
  NOT_CLOSED: 'closure-status closure-status_bad',
  REVIEW_PPK: 'closure-status closure-status_review',
  OUT_OF_GRAPH: 'closure-status closure-status_warn',
  HAS_CONTROL_REQUEST: 'closure-status closure-status_info',
  NOT_FOUND: 'closure-status closure-status_bad',
  DUPLICATE: 'closure-status closure-status_warn',
}

const shortOrgName = (value: string) => {
  const lower = String(value || '').toLowerCase()
  if (lower.includes('тмк')) return 'ТМК'
  if (lower.includes('ниц')) return 'НИЦ НГТ'
  if (lower.includes('эталон')) return 'Эталон'
  if (lower.includes('югра')) return 'Югра'
  return value || 'без подрядчика'
}

const formatDateTime = (value: string) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const importArchiveLabel = (item: any) => {
  const status = item.status === 'APPROVED'
    ? 'проверено'
    : item.status === 'ARCHIVED'
      ? 'архив'
      : 'в работе'
  return `${monthNames[item.month - 1]} ${item.year} — ${shortOrgName(item.org_name)} — ${status} — ${formatDateTime(item.created_at)}`
}

const PageContractorClosure = () => {
  const today = new Date()
  const [orgs, setOrgs] = useState<OrgOption[]>([])
  const [selectedOrg, setSelectedOrg] = useState('')
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [file, setFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<ClosureImport | null>(null)
  const [imports, setImports] = useState<any[]>([])
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [taskStatus, setTaskStatus] = useState<any>(null)
  const [editingRow, setEditingRow] = useState<ClosureRow | null>(null)
  const [editStatus, setEditStatus] = useState('')
  const [editCoefficient, setEditCoefficient] = useState('')
  const [editComment, setEditComment] = useState('')
  const [historyWell, setHistoryWell] = useState<any>(null)
  const [detailsRow, setDetailsRow] = useState<ClosureRow | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [tmkExceptionModalOpen, setTmkExceptionModalOpen] = useState(false)
  const [tmkExceptionText, setTmkExceptionText] = useState('')
  const [tmkExceptionResult, setTmkExceptionResult] = useState<any>(null)
  const [tmkExceptionSaving, setTmkExceptionSaving] = useState(false)
  const [rowsLoading, setRowsLoading] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)

  useEffect(() => {
    Promise.all([
      http.get(API_CONTRACTOR_CLOSURE_OPTIONS),
      http.get(API_CONTRACTOR_CLOSURE_LATEST),
    ])
      .then(([optionsResponse, latestResponse]) => {
        setOrgs(optionsResponse.data.orgs || [])
        if (latestResponse.data) {
          setResult({...latestResponse.data, rows: []})
          setYear(latestResponse.data.year)
          setMonth(latestResponse.data.month)
          setSelectedOrg(latestResponse.data.org || '')
          loadImportRows(latestResponse.data.id)
        }
      })
      .catch(() => setError('Не удалось загрузить данные закрытия КА.'))
  }, [])

  const setImportMeta = (data: any, rows: ClosureRow[] = []) => {
    setResult({...data, rows})
  }

  const loadImportRows = (id: string) => {
    setRowsLoading(true)
    return http.get(API_CONTRACTOR_CLOSURE_IMPORT_ROWS(id))
      .then((response) => {
        setResult((current) => current && current.id === id
          ? {...current, rows: response.data || []}
          : current
        )
        return response.data || []
      })
      .catch(() => setError('Не удалось загрузить строки закрытия КА.'))
      .finally(() => setRowsLoading(false))
  }

  const loadRowDetail = (row: ClosureRow) => {
    setDetailsLoading(true)
    setDetailsRow({...row, details: row.details || null})
    http.get(API_CONTRACTOR_CLOSURE_ROW_DETAIL(row.id))
      .then((response) => setDetailsRow(response.data))
      .catch(() => setError('Не удалось открыть расшифровку строки.'))
      .finally(() => setDetailsLoading(false))
  }

  const loadImports = useCallback((overrides: any = {}) => {
    const params: any = {
      year: overrides.year ?? year,
      month: overrides.month ?? month,
    }
    const orgFilter = overrides.org ?? selectedOrg
    if (orgFilter) {
      params.org = orgFilter
    }
    http.get(API_CONTRACTOR_CLOSURE_IMPORTS, {params}).then((response) => setImports(response.data || []))
  }, [selectedOrg, year, month])

  useEffect(() => {
    loadImports()
  }, [loadImports])

  const rows = useMemo(() => {
    const sourceRows = result?.rows || []
    const statusRows = statusFilter === 'ALL'
      ? sourceRows
      : sourceRows.filter((row) => row.status === statusFilter)
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return statusRows
    }
    return statusRows.filter((row) => rowMatchesSearch(row, query))
  }, [result, statusFilter, searchQuery])

  const counters = result?.summary || {}
  const selectedOrgName = orgs.find((org) => org.id === selectedOrg)?.name || ''
  const isSelectedOrgTmk = selectedOrg ? shortOrgName(selectedOrgName) === 'ТМК' : true
  const isResultOrgTmk = shortOrgName(result?.org_name || '') === 'ТМК'
  const isResultForSelectedOrg = !selectedOrg || !result?.org || result.org === selectedOrg
  const canManageTmkExceptions = Boolean(result && isResultOrgTmk && isSelectedOrgTmk && isResultForSelectedOrg)
  const counterItems = [
    ['Всего', 'total', 'ALL'],
    ['Закрыто', 'closed', 'CLOSED'],
    ['Частично', 'partial', 'PARTIAL'],
    ['Не закрыто', 'not_closed', 'NOT_CLOSED'],
    ['ППК', 'review_ppk', 'REVIEW_PPK'],
    ['Вне графика', 'out_of_graph', 'OUT_OF_GRAPH'],
    ['КС', 'has_control_request', 'HAS_CONTROL_REQUEST'],
    ['Не найдены', 'not_found', 'NOT_FOUND'],
    ['Дубли', 'duplicates', 'DUPLICATE'],
  ]

  const clearCurrentCheck = () => {
    setResult(null)
    setStatusFilter('ALL')
    setSearchQuery('')
    setTaskStatus(null)
    setEditingRow(null)
    setDetailsRow(null)
    setHistoryWell(null)
    setTmkExceptionModalOpen(false)
    setTmkExceptionResult(null)
  }

  const handleOrgChange = (value: string) => {
    setSelectedOrg(value)
    setError('')
    clearCurrentCheck()
  }

  const handleMonthChange = (value: number) => {
    setMonth(value)
    setError('')
    clearCurrentCheck()
  }

  const handleYearChange = (value: number) => {
    setYear(value)
    setError('')
    clearCurrentCheck()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] || null)
    setError('')
    clearCurrentCheck()
  }

  const handleUpload = () => {
    if (!file) {
      setError('Выберите файл закрытия.')
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    formData.append('year', String(year))
    formData.append('month', String(month))
    if (selectedOrg) {
      formData.append('org', selectedOrg)
    }

    setIsLoading(true)
    setError('')
    setTaskStatus(null)
    http.post(API_CONTRACTOR_CLOSURE_IMPORTS, formData)
      .then((response) => {
        setTaskStatus(response.data)
        pollImportStatus(response.data.request_id)
      })
      .catch((uploadError) => {
        const data = uploadError.response?.data
        setError(
          typeof data === 'string'
            ? data
            : data?.org || data?.file || data?.period || data?.month || data?.detail || 'Не удалось проверить файл закрытия.'
        )
        setIsLoading(false)
      })
  }

  const pollImportStatus = (requestId: string) => {
    let attempts = 0
    const timer = window.setInterval(() => {
      attempts += 1
      http.get(API_CONTRACTOR_CLOSURE_IMPORT_STATUS, {params: {request_id: requestId}})
        .then((response) => {
          setTaskStatus(response.data)
          if (response.data.status === 'DONE') {
            window.clearInterval(timer)
            const importId = response.data.import_id
            if (importId) {
              http.get(`${API_CONTRACTOR_CLOSURE_IMPORTS}${importId}/`).then((detailResponse) => {
                setImportMeta(detailResponse.data)
                setYear(detailResponse.data.year)
                setMonth(detailResponse.data.month)
                setSelectedOrg(detailResponse.data.org || '')
                setStatusFilter('ALL')
                return loadImportRows(importId).then(() => loadImports({
                  year: detailResponse.data.year,
                  month: detailResponse.data.month,
                  org: detailResponse.data.org || '',
                }))
              }).finally(() => setIsLoading(false))
            } else {
              setIsLoading(false)
            }
          }
          if (response.data.status === 'ERROR') {
            window.clearInterval(timer)
            setError(response.data.error || response.data.message || 'Проверка завершилась ошибкой.')
            setIsLoading(false)
          }
        })
        .catch(() => {
          window.clearInterval(timer)
          setError('Не удалось получить статус проверки файла.')
          setIsLoading(false)
        })
      if (attempts > 180) {
        window.clearInterval(timer)
        setError('Проверка длится слишком долго. Проверьте Dramatiq worker.')
        setIsLoading(false)
      }
    }, 2000)
  }

  const loadImport = (id: string) => {
    if (!id) return
    http.get(`${API_CONTRACTOR_CLOSURE_IMPORTS}${id}/`).then((response) => {
      setImportMeta(response.data)
      setYear(response.data.year)
      setMonth(response.data.month)
      setSelectedOrg(response.data.org || '')
      setStatusFilter('ALL')
      loadImportRows(id)
    })
  }

  const handleExport = () => {
    if (!result) return
    http.get(`${API_CONTRACTOR_CLOSURE_IMPORTS}${result.id}/export/`, {responseType: 'blob'}).then((response) => {
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `closure_${String(result.month).padStart(2, '0')}_${result.year}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    })
  }

  const handleApprove = () => {
    if (!result) return
    http.patch(`${API_CONTRACTOR_CLOSURE_IMPORTS}${result.id}/`, {status: 'APPROVED'}).then((response) => {
      setResult((current) => current
        ? {...response.data, rows: current.rows || []}
        : {...response.data, rows: []}
      )
      loadImports({
        year: response.data.year,
        month: response.data.month,
        org: response.data.org || '',
      })
    })
  }

  const parseTmkExceptions = () => {
    return tmkExceptionText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(/[;\t]/)
        if (parts.length >= 2) {
          return {
            well: parts[0].trim(),
            comment: parts.slice(1).join('; ').trim(),
          }
        }
        const dashParts = line.split(/\s+-\s+/)
        if (dashParts.length >= 2) {
          return {
            well: dashParts[0].trim(),
            comment: dashParts.slice(1).join(' - ').trim(),
          }
        }
        return {
          well: line,
          comment: '',
        }
      })
  }

  const saveTmkExceptions = () => {
    if (!result) return
    const items = parseTmkExceptions()
    if (!items.length) {
      setTmkExceptionResult({skipped: [{well: '', reason: 'Вставьте хотя бы одну строку'}]})
      return
    }
    setTmkExceptionSaving(true)
    setError('')
    http.post(API_CONTRACTOR_CLOSURE_TMK_EXCEPTIONS(result.id), {items})
      .then((response) => {
        setImportMeta(response.data, response.data.rows || [])
        setTmkExceptionResult(response.data.tmk_exception_result)
        loadImports({
          year: response.data.year,
          month: response.data.month,
          org: response.data.org || '',
        })
      })
      .catch((exceptionError) => {
        const data = exceptionError.response?.data
        setTmkExceptionResult({
          skipped: [{well: '', reason: data?.detail || data?.items || 'Не удалось применить исключения ТМК'}],
        })
      })
      .finally(() => setTmkExceptionSaving(false))
  }

  const openEditRow = (row: ClosureRow) => {
    setEditingRow(row)
    setEditStatus(row.status)
    setEditCoefficient(row.coefficient || '0')
    setEditComment(row.adjustment_comment || '')
  }

  const openWellHistory = (row: ClosureRow) => {
    if (!row.well_id) return
    setHistoryWell({
      wells: {
        id: row.well_id,
        name: row.well_name || row.raw_well,
      },
      bush: row.bush_name || row.raw_bush,
      deposit: row.deposit_name,
      org_name: row.org_name,
    })
  }

  const formatDetailValue = (value: any) => {
    if (value === null || value === undefined || value === '') return '—'
    return String(value)
  }

  const formatPpkDate = (value: string | null | undefined) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('ru-RU')
  }

  const saveEditRow = () => {
    if (!editingRow || !result) return
    setError('')
    http.patch(`${API_CONTRACTOR_CLOSURE_ROWS}${editingRow.id}/adjust/`, {
      status: editStatus,
      coefficient: editCoefficient,
      comment: editComment,
    }).then((response) => {
      const updatedRow = response.data.row
      const summary = response.data.summary
      setResult((current) => {
        if (!current) return current
        return {
          ...current,
          summary: summary || current.summary,
          rows: current.rows.map((row) => row.id === updatedRow.id ? updatedRow : row),
        }
      })
      setEditingRow(null)
    }).catch((editError) => {
      const data = editError.response?.data
      setError(data?.comment || data?.coefficient || data?.status || 'Не удалось сохранить корректировку.')
    })
  }

  return (
    <div className="closure-page">
      <div className="closure-header">
        <div>
          <h1>Закрытие КА по скребкованию</h1>
          <p>Проверка актов подрядчиков по графику, ППК и заявкам КС</p>
        </div>
      </div>

      <div className="closure-toolbar">
        <label>
          Подрядчик
          <select value={selectedOrg} onChange={(event) => handleOrgChange(event.target.value)}>
            <option value="">Определить по файлу</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </label>
        <label>
          Месяц
          <select value={month} onChange={(event) => handleMonthChange(Number(event.target.value))}>
            {monthNames.map((label, index) => (
              <option key={label} value={index + 1}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          Год
          <input value={year} type="number" onChange={(event) => handleYearChange(Number(event.target.value))}/>
        </label>
        <label className="closure-file-field">
          Файл закрытия
          <span className="closure-file-control">
            <input type="file" accept=".xlsx,.xls" onChange={handleFileChange}/>
            <span className="closure-file-button">Выбрать</span>
            <span className="closure-file-name">{file?.name || 'Файл не выбран'}</span>
          </span>
        </label>
        <button className="closure-primary" disabled={isLoading} onClick={handleUpload}>
          {isLoading ? 'Проверяем...' : 'Проверить файл'}
        </button>
      </div>

      {error && <div className="closure-error">{error}</div>}

      {taskStatus && (
        <div className={`closure-task closure-task_${String(taskStatus.status || '').toLowerCase()}`}>
          <div>
            <strong>{taskStatus.message || 'Проверка файла'}</strong>
            <span>{taskStatus.status}</span>
          </div>
          {taskStatus.summary && (
            <div className="closure-task-summary">
              Закрыто: {taskStatus.summary.closed || 0}, не закрыто: {taskStatus.summary.not_closed || 0},
              ППК: {taskStatus.summary.review_ppk || 0}
            </div>
          )}
        </div>
      )}

      {(result || imports.length > 0) && (
        <div className="closure-actions">
          <label>
            Архив проверок
            <select value={result?.id || ''} onChange={(event) => loadImport(event.target.value)}>
              <option value="">Выберите проверку</option>
              {imports.map((item) => (
                <option key={item.id} value={item.id}>
                  {importArchiveLabel(item)}
                </option>
              ))}
            </select>
          </label>
          {result && (
            <>
              <button onClick={handleExport}>Выгрузить результат</button>
              {canManageTmkExceptions && (
                <button onClick={() => {
                  setTmkExceptionModalOpen(true)
                  setTmkExceptionResult(null)
                }}>
                  Исключения ТМК
                </button>
              )}
              <button className="closure-approve" onClick={handleApprove} disabled={result.status === 'APPROVED'}>
                {result.status === 'APPROVED' ? 'Проверено' : 'Завершить проверку'}
              </button>
            </>
          )}
        </div>
      )}

      {result && (
        <>
          <div className="closure-meta">
            <span>{result.source_file}</span>
            <span>{result.org_name || 'Подрядчик определялся автоматически'}</span>
            <span>{monthNames[result.month - 1]} {result.year}</span>
            <span>{result.status === 'APPROVED' ? 'Проверено' : 'В работе'}</span>
          </div>

          <div className="closure-counters">
            {counterItems.map(([label, key, status]) => (
              <button
                key={key}
                className={statusFilter === status ? 'closure-counter closure-counter_active' : 'closure-counter'}
                onClick={() => setStatusFilter(status)}
              >
                <span>{label}</span>
                <strong>{counters[key] || 0}</strong>
              </button>
            ))}
          </div>

          <div className="closure-search-panel">
            <label>
              <span>Поиск по таблице</span>
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Скважина, куст, ДНС, КС, статус или комментарий"
              />
            </label>
            <div>
              Показано <strong>{rows.length}</strong> из <strong>{result.rows.length}</strong>
            </div>
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')}>Сбросить</button>
            )}
          </div>

          <div className="closure-table-wrap">
            <table className="closure-table">
              <thead>
              <tr>
                <th>Статус</th>
                <th>Лист</th>
                <th>Строка</th>
                <th>Месторождение</th>
                <th>ДНС</th>
                <th>Куст</th>
                <th>Скважина</th>
                <th>План</th>
                <th>Факт</th>
                <th>Глубина</th>
                <th>Коэф.</th>
                <th>КС</th>
                <th>Комментарий системы</th>
                <th>Комментарий подрядчика</th>
                <th>Корректировка</th>
                <th>Действие</th>
              </tr>
              </thead>
              <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <button
                      type="button"
                      className="closure-status-button"
                      onClick={() => loadRowDetail(row)}
                      title="Открыть расшифровку проверки"
                    >
                      <span className={statusClass[row.status] || 'closure-status'}>{row.status_label}</span>
                    </button>
                  </td>
                  <td>{row.sheet_name}</td>
                  <td>{row.excel_row}</td>
                  <td>{row.deposit_name || '—'}</td>
                  <td>{row.raw_dns || '—'}</td>
                  <td>{row.bush_name || row.raw_bush || '—'}</td>
                  <td>
                    {row.well_id ? (
                      <button
                        type="button"
                        className="closure-well-link"
                        onClick={() => openWellHistory(row)}
                        title="Открыть историю графика скважины"
                      >
                        {row.well_name || row.raw_well}
                      </button>
                    ) : (
                      row.well_name || row.raw_well
                    )}
                  </td>
                  <td>{row.plan_from_file || '—'}</td>
                  <td>{row.fact_from_file || '—'}</td>
                  <td>{row.depth_from_file || '—'}</td>
                  <td>{row.coefficient}</td>
                  <td>{row.control_request_number || '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="closure-comment-link"
                      onClick={() => loadRowDetail(row)}
                      title="Открыть расшифровку проверки"
                    >
                      {row.system_comment}
                    </button>
                  </td>
                  <td>{row.contractor_comment || '—'}</td>
                  <td>{row.adjustment_comment || '—'}</td>
                  <td><button className="closure-row-action" onClick={() => openEditRow(row)}>Изменить</button></td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={16} className="closure-empty">
                    {rowsLoading ? 'Загружаем строки...' : 'Нет строк для выбранного фильтра'}
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editingRow && (
        <div className="closure-modal-backdrop">
          <div className="closure-modal">
            <h2>Корректировка {editingRow.raw_well}</h2>
            <label>
              Статус
              <select value={editStatus} onChange={(event) => setEditStatus(event.target.value)}>
                <option value="CLOSED">Закрыта</option>
                <option value="PARTIAL">Частично</option>
                <option value="NOT_CLOSED">Не закрыта</option>
                <option value="REVIEW_PPK">Проверить ППК</option>
                <option value="OUT_OF_GRAPH">Вне графика</option>
                <option value="HAS_CONTROL_REQUEST">Есть заявка КС</option>
                <option value="NOT_FOUND">Не найдена</option>
                <option value="DUPLICATE">Дубль в файле</option>
              </select>
            </label>
            <label>
              Коэффициент
              <input value={editCoefficient} onChange={(event) => setEditCoefficient(event.target.value)}/>
            </label>
            <label>
              Причина корректировки
              <textarea value={editComment} onChange={(event) => setEditComment(event.target.value)}/>
            </label>
            <div className="closure-modal-actions">
              <button onClick={() => setEditingRow(null)}>Отмена</button>
              <button className="closure-primary" onClick={saveEditRow}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {historyWell && (
        <GraphWellHistoryModal
          row={historyWell}
          dayStateOptions={{mode: 'closure'}}
          onClose={() => setHistoryWell(null)}
        />
      )}

      {tmkExceptionModalOpen && result && (
        <div className="closure-modal-backdrop">
          <div className="closure-modal closure-modal_wide">
            <h2>Согласованные исключения ТМК</h2>
            <p className="closure-modal-hint">
              Вставьте скважины, по которым согласован коэффициент 1 при двух подходах.
              Формат строки: <strong>скважина; причина</strong>.
            </p>
            <label>
              Список скважин
              <textarea
                value={tmkExceptionText}
                onChange={(event) => setTmkExceptionText(event.target.value)}
                placeholder={'12275; легкая скважина, 2 подхода достаточно\n37Р; согласовано с куратором'}
              />
            </label>

            {!!(result.tmk_exceptions || []).length && (
              <div className="closure-exception-list">
                <h3>Уже внесены</h3>
                {(result.tmk_exceptions || []).map((item) => (
                  <div key={item.id}>
                    <strong>{item.well_name || item.raw_well}</strong>
                    <span>{item.comment}</span>
                    <small>{item.created_by_name || '—'} · {formatDateTime(item.created_at)}</small>
                  </div>
                ))}
              </div>
            )}

            {tmkExceptionResult && (
              <div className="closure-exception-result">
                {!!(tmkExceptionResult.applied || []).length && (
                  <div>
                    <h3>Применено</h3>
                    {(tmkExceptionResult.applied || []).map((item: any, index: number) => (
                      <span key={`${item.well}-${index}`}>{item.well}</span>
                    ))}
                  </div>
                )}
                {!!(tmkExceptionResult.skipped || []).length && (
                  <div className="closure-exception-skipped">
                    <h3>Не применено</h3>
                    {(tmkExceptionResult.skipped || []).map((item: any, index: number) => (
                      <p key={`${item.well}-${index}`}>
                        <strong>{item.well || `Строка ${item.line || index + 1}`}</strong> — {item.reason}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="closure-modal-actions">
              <button onClick={() => setTmkExceptionModalOpen(false)}>Закрыть</button>
              <button className="closure-primary" disabled={tmkExceptionSaving} onClick={saveTmkExceptions}>
                {tmkExceptionSaving ? 'Применяем...' : 'Применить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsRow && (
        <div className="closure-modal-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setDetailsRow(null)
        }}>
          <div className="closure-detail-modal">
            <div className="closure-detail-head">
              <div>
                <h2>Расшифровка {detailsRow.well_name || detailsRow.raw_well}</h2>
                <span>
                  {detailsRow.deposit_name || '—'} · куст {detailsRow.bush_name || detailsRow.raw_bush || '—'} · строка Excel {detailsRow.excel_row}
                </span>
              </div>
              <button type="button" onClick={() => setDetailsRow(null)} aria-label="Закрыть">×</button>
            </div>

            <div className="closure-detail-grid">
              <div className="closure-detail-card">
                <h3>Решение системы</h3>
                <dl>
                  <dt>Статус</dt>
                  <dd><span className={statusClass[detailsRow.status] || 'closure-status'}>{detailsRow.status_label}</span></dd>
                  <dt>Коэффициент</dt>
                  <dd>{detailsRow.coefficient}</dd>
                  <dt>Подрядчик графика</dt>
                  <dd>{detailsRow.details?.graph_org || detailsRow.org_name || '—'}</dd>
                  <dt>Заявка КС</dt>
                  <dd>{detailsRow.control_request_number || '—'}</dd>
                </dl>
                <p>{detailsRow.system_comment}</p>
                {detailsLoading && <small>Загружаем расшифровку...</small>}
              </div>

              <div className="closure-detail-card">
                <h3>Данные из файла</h3>
                <dl>
                  <dt>Лист</dt>
                  <dd>{detailsRow.sheet_name || '—'}</dd>
                  <dt>План</dt>
                  <dd>{detailsRow.plan_from_file || '—'}</dd>
                  <dt>Факт</dt>
                  <dd>{detailsRow.fact_from_file || '—'}</dd>
                  <dt>Глубина</dt>
                  <dd>{detailsRow.depth_from_file || '—'}</dd>
                </dl>
                <p>{detailsRow.contractor_comment || 'Комментарий подрядчика отсутствует.'}</p>
              </div>
            </div>

            <div className="closure-detail-card">
              <h3>Итоги по графику</h3>
              <div className="closure-detail-pills">
                <span>Засчитано проходок: <strong>{detailsRow.details?.graph?.pass_days ?? '—'}</strong></span>
                <span>Оргпричин: <strong>{detailsRow.details?.graph?.org_reason_days ?? '—'}</strong></span>
                <span>Макс. глубина: <strong>{formatDetailValue(detailsRow.details?.graph?.max_depth)}</strong></span>
                <span>Правило: <strong>{detailsRow.details?.kind === 'TMK' ? 'ТМК, 3 подхода' : '1 проходка за месяц'}</strong></span>
              </div>
            </div>

            {detailsRow.details?.ppk && (
              <div className="closure-detail-card closure-detail-card_warn">
                <h3>ППК</h3>
                <dl>
                  <dt>Дата акта</dt>
                  <dd>{formatPpkDate(detailsRow.details.ppk.ppk_date)}</dd>
                  <dt>Подтверждение</dt>
                  <dd>{formatPpkDate(detailsRow.details.ppk.ppk_commit_date)}</dd>
                  <dt>Глубина ППК</dt>
                  <dd>{formatDetailValue(detailsRow.details.ppk.depth)}</dd>
                  <dt>Дни с отметкой ППК</dt>
                  <dd>{(detailsRow.details.ppk.ppk_comment_days || []).join(', ') || '—'}</dd>
                </dl>
              </div>
            )}

            {!!detailsRow.details?.graph?.ignored_manual_days?.length && (
              <div className="closure-detail-card closure-detail-card_bad">
                <h3>Ручной ввод не засчитан</h3>
                <div className="closure-detail-list">
                  {detailsRow.details.graph.ignored_manual_days.map((item: any) => (
                    <div key={`${item.day}-${item.depth}`}>
                      <strong>{item.day} число · {item.depth} м</strong>
                      <span>{item.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="closure-detail-card">
              <h3>Дни с событиями</h3>
              <div className="closure-detail-days">
                {(detailsRow.details?.graph?.day_details || []).map((item: any) => (
                  <div key={`${detailsRow.id}-${item.day}`} className="closure-detail-day">
                    <strong>{item.day}</strong>
                    <span>план {formatDetailValue(item.plan)}</span>
                    <span>факт {formatDetailValue(item.fact)}</span>
                    <span>{formatDetailValue(item.depth)} м</span>
                    {item.comment && <em>{item.comment}</em>}
                  </div>
                ))}
                {!(detailsRow.details?.graph?.day_details || []).length && (
                  <div className="closure-detail-empty">В графике нет событий по этой строке.</div>
                )}
              </div>
            </div>

            {detailsRow.adjustment_comment && (
              <div className="closure-detail-card">
                <h3>Ручная корректировка</h3>
                <p>{detailsRow.adjustment_comment}</p>
                <small>
                  {detailsRow.adjusted_by_name || '—'} · {detailsRow.adjusted_at ? formatPpkDate(detailsRow.adjusted_at) : '—'}
                </small>
              </div>
            )}

            <div className="closure-detail-card">
              <h3>Журнал корректировок</h3>
              <div className="closure-adjustment-log">
                {(detailsRow.adjustment_logs || []).map((item: any) => (
                  <div key={item.id}>
                    <strong>
                      {item.old_status_label} / {item.old_coefficient} → {item.new_status_label} / {item.new_coefficient}
                    </strong>
                    <span>{item.comment}</span>
                    <small>{item.changed_by_name || '—'} · {formatDateTime(item.created_at)}</small>
                  </div>
                ))}
                {!(detailsRow.adjustment_logs || []).length && (
                  <div className="closure-detail-empty">Корректировок по строке еще не было.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function rowMatchesSearch(row: ClosureRow, query: string) {
  const searchable = [
    row.sheet_name,
    row.excel_row,
    row.raw_dns,
    row.raw_bush,
    row.raw_well,
    row.plan_from_file,
    row.fact_from_file,
    row.depth_from_file,
    row.contractor_comment,
    row.org_name,
    row.well_name,
    row.bush_name,
    row.deposit_name,
    row.status_label,
    row.coefficient,
    row.system_comment,
    row.adjustment_comment,
    row.adjusted_by_name,
    row.control_request_number,
    row.details?.graph_org,
    row.details?.kind,
    ...(row.details?.graph?.day_details || []).flatMap((item: any) => [
      item.day,
      item.plan,
      item.fact,
      item.depth,
      item.comment,
    ]),
  ]
  return searchable.some((value) => String(value ?? '').toLowerCase().includes(query))
}

export default PageContractorClosure
